/// <reference path="../react-table-config.d.ts" />
import { cloneDeep } from '@apollo/client/utilities/common/cloneDeep'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { Button, Grid } from '@material-ui/core'
import { CSSProperties } from '@material-ui/core/styles/withStyles'
import { ArrowBack, ArrowForward } from '@material-ui/icons'
import clsx from 'clsx'
import React, { useState } from 'react'
import { Row } from 'react-table'
import { TableBodyProps } from './TableBody'
import { tableBodyStyles } from './tableBody.styles'
import TableRow from './TableRow'

export interface TableChildrenBodyProps<TData extends Object>
    extends TableBodyProps<TData> {
    isPaginationRequired: boolean
    childTablePageSize: number
}

export const handlePageChange = (
    newPage: number,
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>,
    totalPages: number
) => {
    if (newPage < 0) {
        setCurrentPage(totalPages - 1)
    } else if (newPage >= totalPages) {
        setCurrentPage(0)
    } else {
        setCurrentPage(newPage)
    }
}

interface PaginationComponentProps {
    cellCssOverride?: CSSProperties
    handlePreviousPageChange: () => void
    handleNextPageChange: () => void
    currentPage: number
    totalPages: number
    totalRowsCount: number
    childRowsCount: number
}

const TableChildrenBody = <TData extends Object>({
    cellCssOverride,
    checkboxColumnWidth,
    prepareRow,
    rows,
    LoadNextComponent,
    SkeletonComponent,
    isPaginationRequired,
    childTablePageSize,
}: TableChildrenBodyProps<TData>) => {
    const [currentPage, setCurrentPage] = useState(0)
    const pageSize = childTablePageSize || 10
    const totalPages = isPaginationRequired
        ? Math.ceil(rows?.length / pageSize)
        : 0

    const childRows = cloneDeep(
        rows
            .filter((row) => !row.isGrouped)
            .slice(currentPage * pageSize, (currentPage + 1) * pageSize)
    )

    const paginationControls = (isPaginationRequired && (
        <PaginationComponent
            cellCssOverride={cellCssOverride}
            handlePreviousPageChange={() =>
                handlePageChange(currentPage - 1, setCurrentPage, totalPages)
            }
            handleNextPageChange={() =>
                handlePageChange(currentPage + 1, setCurrentPage, totalPages)
            }
            currentPage={currentPage}
            totalPages={totalPages}
            totalRowsCount={rows.length}
            childRowsCount={childRows.length}
        />
    )) as React.ReactNode

    return (
        <Grid data-testid="tableChildrenBody">
            {paginationControls}
            <Grid>
                {childRows.map((row: Row<TData>) => {
                    prepareRow(row)

                    return (
                        <TableRow
                            cellCssOverride={cellCssOverride}
                            checkboxColumnWidth={checkboxColumnWidth}
                            row={row}
                            dataTestId="tableRowChildren"
                        />
                    )
                })}
            </Grid>
            {!!LoadNextComponent ? LoadNextComponent : <></>}
            {!!SkeletonComponent ? SkeletonComponent : <></>}
        </Grid>
    )
}

const PaginationComponent = ({
    cellCssOverride,
    handlePreviousPageChange,
    handleNextPageChange,
    currentPage,
    totalPages,
    totalRowsCount,
    childRowsCount,
}: PaginationComponentProps) => {
    const classes = tableBodyStyles(cellCssOverride)
    return (
        <GridItem className={classes.paginationContainer} role="row">
            <div role="cell">
                <span
                    data-testid="rowsInformation"
                    className={classes.paginationRecordsSpan}
                >
                    Showing {childRowsCount} of {totalRowsCount} rows &nbsp;
                </span>
                &nbsp;&nbsp;
                <Button
                    className={clsx(classes.paginationButton)}
                    data-testid="btnPrevious"
                    variant="outlined"
                    startIcon={<ArrowBack />}
                    onClick={handlePreviousPageChange}
                    classes={{
                        label: classes.buttonLabel,
                    }}
                >
                    Previous
                </Button>
                &nbsp;&nbsp;
                <span>
                    Page {currentPage + 1} of {totalPages}
                </span>
                &nbsp;&nbsp;
                <Button
                    className={clsx(classes.paginationButton)}
                    data-testid="btnNext"
                    variant="outlined"
                    endIcon={<ArrowForward />}
                    onClick={handleNextPageChange}
                    classes={{
                        label: classes.buttonLabel,
                    }}
                >
                    Next
                </Button>
            </div>
        </GridItem>
    )
}
export default TableChildrenBody
